import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { loadInitialTheme, watchSystemTheme } from './lib/theme';

// ── Silence the benign "ResizeObserver loop …" browser warning ──────────────
// The observer merely skipped a frame; it's harmless and commonly fires from
// popover / select positioning (Radix + floating-ui). CRA's dev overlay would
// otherwise surface it as an uncaught runtime error. Two guards:
//   1) Defer ResizeObserver callbacks to requestAnimationFrame so the
//      synchronous re-layout loop that produces the warning never happens.
//   2) Swallow the error event as a backstop so the dev overlay never shows it.
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}
window.addEventListener(
  'error',
  (e) => {
    if (typeof e.message === 'string' && e.message.includes('ResizeObserver loop')) {
      e.stopImmediatePropagation();
    }
  },
  true,
);

// Set data-theme on <html> before React renders so the correct CSS variables
// are applied on the very first paint (no flash of light→dark).
loadInitialTheme();
// Keep following the OS colour-scheme until the user makes an explicit choice.
watchSystemTheme();

// Flag the document for the Electron desktop build so CSS reserves space for
// the custom title bar. macOS keeps native traffic lights (`os-mac`); other
// desktop platforms get renderer-drawn window controls (`os-custom-controls`).
if (typeof window !== 'undefined' && window.electronAPI?.isElectron) {
  const root = document.documentElement;
  root.classList.add('is-desktop-electron');
  root.classList.add(
    window.electronAPI.platform === 'darwin' ? 'os-mac' : 'os-custom-controls',
  );
}

// Renderer-side error reporter. Console output is piped through
// electron-log into <userData>/logs/main.log — gives us a real stack
// trace from user-reported white-screen incidents.
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    console.error(
      '[window error]',
      e.message,
      e.filename,
      e.lineno,
      e.colno,
      e.error?.stack,
    );
  });
  window.addEventListener('unhandledrejection', (e) => {
    console.error('[unhandled rejection]', e.reason);
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Signal the Electron main process that React mounted successfully.
// Double-defer (rAF + setTimeout) so we ping AFTER the first paint, not
// just after React's commit phase.
if (typeof window !== 'undefined' && window.electronAPI?.reportMounted) {
  requestAnimationFrame(() => {
    setTimeout(() => {
      try { window.electronAPI.reportMounted(); } catch (_e) {}
    }, 0);
  });
}

// Service worker: register only in production builds. In CRA dev the SW
// caches stale chunks. Unregister any leftover SW in dev to avoid stale bundles.
if ('serviceWorker' in navigator) {
  if (process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    });
  } else {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    }).catch(() => {});
    if (window.caches && caches.keys) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
    }
  }
}
